import React from 'react';
import { X, Mail, Phone, Building2, Calendar, FileText } from 'lucide-react';
import type { Contact } from '../../../types/contacts';

interface ContactDetailsProps {
  contact: Contact;
  onClose: () => void;
  onEdit: () => void;
}

export default function ContactDetails({ contact, onClose, onEdit }: ContactDetailsProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Contact Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="border-b border-gray-100 pb-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-medium text-gray-900">
                  {contact.first_name} {contact.last_name}
                </h3>
                {contact.title && (
                  <p className="text-gray-600 mt-1">{contact.title}</p>
                )}
              </div>
              <button
                onClick={onEdit}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                Edit Contact
              </button>
            </div>
          </div>

          {/* Organization */}
          {contact.organization && (
            <div className="flex items-start space-x-3">
              <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900">Organization</h4>
                <p className="text-gray-600">{contact.organization.name}</p>
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-4">
            {contact.emails && contact.emails.length > 0 && (
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Email Addresses</h4>
                  <div className="space-y-2 mt-1">
                    {contact.emails.map((email) => (
                      <div key={email.id} className="text-gray-600">
                        <a 
                          href={`mailto:${email.email}`}
                          className="hover:text-blue-500"
                        >
                          {email.email}
                        </a>
                        <span className="text-gray-400 text-sm ml-2">
                          ({email.type})
                          {email.is_primary && " • Primary"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {contact.phones && contact.phones.length > 0 && (
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Phone Numbers</h4>
                  <div className="space-y-2 mt-1">
                    {contact.phones.map((phone) => (
                      <div key={phone.id} className="text-gray-600">
                        <a 
                          href={`tel:${phone.phone}`}
                          className="hover:text-blue-500"
                        >
                          {phone.phone}
                        </a>
                        <span className="text-gray-400 text-sm ml-2">
                          ({phone.type})
                          {phone.is_primary && " • Primary"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {contact.notes && (
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900">Notes</h4>
                <p className="text-gray-600 mt-1 whitespace-pre-line">
                  {contact.notes}
                </p>
              </div>
            </div>
          )}

          {/* Source */}
          {contact.source && (
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900">Source</h4>
                <p className="text-gray-600 mt-1">
                  {contact.source === 'gekko' ? 'Gekko (Manual Entry)' :
                   contact.source === 'import' ? 'Imported' :
                   contact.source === 'csv' ? 'CSV Import' :
                   contact.source === 'google' ? 'Google Contacts' :
                   contact.source === 'outlook' ? 'Outlook' :
                   contact.source === 'salesforce' ? 'Salesforce' :
                   contact.source === 'hubspot' ? 'HubSpot' :
                   contact.source === 'api' ? 'API' :
                   contact.source === 'manual' ? 'Manual Entry' :
                   contact.source}
                </p>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="flex items-center space-x-3 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>
                Created on {new Date(contact.created_at).toLocaleDateString()}
              </span>
              {contact.updated_at && (
                <>
                  <span>•</span>
                  <span>
                    Last updated {new Date(contact.updated_at).toLocaleDateString()}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}